import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, AlertCircle, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { requestPasswordReset } from '../../../core/services/passwordResetService';

const PREAUTH_SHELL =
  'qawaem-preauth-route flex min-h-screen w-full flex-col items-center overflow-x-hidden bg-gradient-to-br from-slate-900 via-blue-750 to-slate-900 px-4 py-8 sm:py-12';

const CARD_OUTER =
  'relative w-full overflow-hidden rounded-3xl border border-white/20 bg-white/10 shadow-xl shadow-black/25 backdrop-blur-lg';

const QAWAEM_FAVICON_SRC = encodeURI(
  `${process.env.PUBLIC_URL ?? ''}/qawaem/favicon.png`
);

const ForgotPasswordPage = ({ initialEmail = '' }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`setup.forgotPassword.${key}`, opts);
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim();
    if (!trimmed) {
      setError(p('errors.enter_email'));
      return;
    }
    if (trimmed.includes('@') && !/\S+@\S+\.\S+/.test(trimmed)) {
      setError(p('errors.invalid_email'));
      return;
    }
    setLoading(true);
    try {
      const result = await requestPasswordReset(trimmed);
      if (result.success) {
        setSent(true);
        navigate('/reset-password', { state: { email: trimmed } });
      } else {
        setError(
          result.error ||
            (result.code === 'NETWORK_ERROR'
              ? p('errors.network')
              : p('errors.send_failed')),
        );
      }
    } catch (err) {
      setError(err?.message || p('errors.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={PREAUTH_SHELL}>
      <div className={`mx-auto w-full max-w-lg p-6 sm:p-10 ${CARD_OUTER}`}>
        <div className="relative">
          <div className="mb-8 text-center">
            <motion.img
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              src={QAWAEM_FAVICON_SRC}
              alt="Qawaem"
              className="mx-auto mb-5 h-14 w-14 object-contain sm:h-16 sm:w-16"
              draggable={false}
            />
            <h1 className="text-2xl font-bold text-white sm:text-3xl">{p('title')}</h1>
            <p className="mt-2 text-sm text-white/60 sm:text-base">
              {p('subtitle')}
            </p>
          </div>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-5 text-center"
            >
              <CheckCircle className="mx-auto mb-3 h-10 w-10 text-emerald-300" />
              <p
                className="text-emerald-100"
                dangerouslySetInnerHTML={{ __html: p('code_sent') }}
              />
              <p className="mt-2 text-sm text-white/55">{p('redirecting')}</p>
              <Link
                to="/login"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-blue-300 hover:text-blue-200"
              >
                <ChevronLeft className="h-4 w-4" />
                {p('back_to_login')}
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-start gap-2 rounded-xl border border-red-500/35 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label htmlFor="reset-email" className="mb-2 block text-sm font-medium text-white/70">
                  {p('email_label')}
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35">
                    <Mail className="h-5 w-5" />
                  </span>
                  <input
                    id="reset-email"
                    type="text"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder={p('email_placeholder')}
                    autoComplete="email"
                    autoFocus
                    className="w-full rounded-xl border border-white/20 bg-white/5 py-3.5 pl-12 pr-4 text-white placeholder:text-white/35 focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
                <p className="mt-2 text-xs text-white/45">
                  {p('email_hint')}
                </p>
              </div>

              <div className="flex flex-col items-center gap-3 pt-2">
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={loading ? {} : { scale: 1.03 }}
                  whileTap={loading ? {} : { scale: 0.97 }}
                  className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-blue-500/25 disabled:opacity-55"
                >
                  {loading ? p('sending') : (
                    <>
                      {p('send_code')}
                      <ChevronRight className="h-5 w-5" />
                    </>
                  )}
                </motion.button>
                <Link to="/login" className="text-sm text-white/50 hover:text-white/75">
                  {p('back_to_login')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
